import { datesOf, weekday, type IsoDate } from '../dates';
import type { Plan, PlannedDay, PlanWarning } from '../plan';
import { createRandom } from '../random';
import { portionNutrition, type Recipe } from '../recipe';
import { tripDates, tripFor, type ShoppingContext, type ShoppingResult } from '../shopping';
import { withClosedShopWarnings } from './closed-shops';
import { stockFromPantry, type Stock } from '../stock';
import { toCents } from '../money';
import { periodBudget } from './budget';
import { dayRules, relaxedCandidates } from './candidates';
import { consumptionCents, purchaseCents, scaledIngredients } from './costs';
import { countFish, wantsFish, type FishCounts } from './fish';
import { addOnTakers, assignAddOns, recordAddOns, type AddOnCounts } from './meat';
import { repairBudget } from './repair';
import { recipeOf, shopForPlan, shoppingContext, type PlanRequest } from './request';
import { scoreDinner, type DinnerHistoryEntry } from './scoring';

export type { PlanRequest } from './request';

export class PlanningError extends Error {
  constructor(readonly code: 'no-allowed-recipes') {
    super(`Planning failed: ${code}`);
    this.name = 'PlanningError';
  }
}

interface DraftState {
  request: PlanRequest;
  context: ShoppingContext;
  random: () => number;
  trips: IsoDate[];
  stock: Stock;
  history: DinnerHistoryEntry[];
  warnings: PlanWarning[];
  counts: AddOnCounts;
  fishPerWeek: FishCounts;
  breakfastTurns: { workday: number; weekend: number };
  targetPortionCents: number;
  servings: { breakfast: number; dinner: number };
}

// DE: Bonus für Gerichte mit Fleischbeilage, solange jemand diese Woche noch eine möchte.
// EN: Bonus for dinners with a meat add-on while someone still wants one this week.
const ADD_ON_BONUS = 0.5;
// DE: Bonus für Fisch, wenn laut Wochenplan ein Fischgericht fällig ist; gleicht einen Mehrpreis
// von einer Richtportion aus (Kostengewicht 1,5).
// EN: Bonus for fish when a fish dinner is due by the weekly schedule; it outweighs an extra cost
// of one target portion (cost weight 1.5).
const FISH_BONUS = 1.5;
// DE: Anteil des Budgets, der als Richtwert für Abend- und Mittagessen dient.
// EN: Share of the budget used as the guide value for dinners and lunches.
const DINNER_BUDGET_SHARE = 0.7;
// DE: Untergrenze des Richtwerts, damit ein Budget von 0 € nicht durch null teilt.
// EN: Lower bound of the guide value so that a budget of 0 € never divides by zero.
const MIN_TARGET_CENTS = 1;

function createState(request: PlanRequest): DraftState {
  // DE: Ohne Personen gibt es keine Portionen; der Fehler nennt die Ursache statt fehlender Rezepte.
  // EN: Without members there are no portions; the error names the cause, not missing recipes.
  if (request.household.members.length === 0) {
    throw new Error('At least one household member is required');
  }
  const factors = request.household.members.reduce((sum, member) => sum + member.portionFactor, 0);
  const servings = { breakfast: factors, dinner: 2 * factors };
  const budgetCents = toCents(periodBudget(request.household, request.period));
  const context = shoppingContext(request);
  return {
    request,
    context,
    random: createRandom(request.seed),
    trips: tripDates(context),
    stock: [
      ...(request.carryOver?.stock ?? []),
      ...stockFromPantry(request.pantry, request.period.start, request.book.catalog),
    ],
    history: [],
    warnings: [],
    counts: new Map(),
    fishPerWeek: new Map(),
    breakfastTurns: { workday: 0, weekend: 0 },
    targetPortionCents: Math.max(
      MIN_TARGET_CENTS,
      (DINNER_BUDGET_SHARE * budgetCents) / (request.period.days * servings.dinner),
    ),
    servings,
  };
}

function consume(
  state: DraftState,
  needs: { productId: string; quantity: number }[],
  date: IsoDate,
) {
  const dates = { need: date, trip: tripFor(state.trips, date) };
  state.stock = purchaseCents(needs, dates, state.stock, state.context).stock;
}

function breakfastKind(date: IsoDate, state: DraftState): 'workday' | 'weekend' {
  return state.request.household.workdays.includes(weekday(date)) ? 'workday' : 'weekend';
}

// DE: Beim Weiterplanen zählen die festen vergangenen Tage für Abwechslung, Fisch, Fleisch und Frühstück.
// EN: When planning on, the fixed past days count for variety, fish, meat and breakfasts.
function seedFromPastDays(state: DraftState) {
  const { request } = state;
  for (const day of request.carryOver?.pastDays ?? []) {
    const recipe = recipeOf(request, day.dinner.recipeId);
    state.history.push({ date: day.date, recipeId: recipe.id, cuisine: recipe.cuisine });
    countFish(recipe, day.date, { catalog: request.book.catalog, counts: state.fishPerWeek });
    recordAddOns(day.date, day.dinner.meatAddOns, state.counts);
    state.breakfastTurns[breakfastKind(day.date, state)] += 1;
  }
}

function pickBreakfast(date: IsoDate, state: DraftState) {
  const { breakfasts } = state.request.household;
  const kind = breakfastKind(date, state);
  const list = breakfasts[kind];
  const recipeId = list[state.breakfastTurns[kind] % list.length];
  if (!recipeId) throw new Error(`No ${kind} breakfast configured`);
  state.breakfastTurns[kind] += 1;
  return { recipeId, servings: state.servings.breakfast };
}

function candidateScore(
  recipe: Recipe,
  date: IsoDate,
  state: DraftState,
  history: DinnerHistoryEntry[],
) {
  const { request } = state;
  const servings = state.servings.dinner;
  const dates = { need: date, trip: tripFor(state.trips, date) };
  const needs = scaledIngredients(recipe, servings);
  const marginal = purchaseCents(needs, dates, state.stock, state.context).cents;
  const consumption = consumptionCents(recipe, servings, state.context, date);
  const takers = addOnTakers(recipe, date, request.household.members, {
    catalog: request.book.catalog,
    counts: state.counts,
  });
  const score = scoreDinner({
    recipe,
    date,
    history,
    portionCostCents: (0.5 * marginal + 0.5 * consumption) / servings,
    targetPortionCents: state.targetPortionCents,
    nutrition: portionNutrition(recipe, request.book.catalog, request.nutrition),
    random: state.random,
  });
  const fish = wantsFish(recipe, date, {
    catalog: request.book.catalog,
    target: request.household.fishDinnersPerWeek,
    counts: state.fishPerWeek,
  });
  return score + (takers.length > 0 ? ADD_ON_BONUS : 0) + (fish ? FISH_BONUS : 0);
}

function bestOf(recipes: readonly Recipe[], score: (recipe: Recipe) => number) {
  return recipes.reduce<{ recipe: Recipe | undefined; score: number }>(
    (best, recipe) => {
      const value = score(recipe);
      return value > best.score ? { recipe, score: value } : best;
    },
    { recipe: undefined, score: -Infinity },
  );
}

function chooseDinner(date: IsoDate, state: DraftState): Recipe {
  const { household, recipes, book } = state.request;
  const { recipes: candidates, warning } = relaxedCandidates(
    recipes,
    household,
    book.catalog,
    dayRules(date, household),
  );
  if (warning) state.warnings.push(warning);
  const best = bestOf(candidates, (recipe) => candidateScore(recipe, date, state, state.history));
  if (best.recipe) return best.recipe;
  state.warnings.push({ code: 'relaxed-variety', date });
  const fallback = bestOf(candidates, (recipe) => candidateScore(recipe, date, state, []));
  if (!fallback.recipe) throw new PlanningError('no-allowed-recipes');
  return fallback.recipe;
}

function planDay(date: IsoDate, state: DraftState): PlannedDay {
  const { request } = state;
  const breakfast = pickBreakfast(date, state);
  consume(
    state,
    scaledIngredients(recipeOf(request, breakfast.recipeId), breakfast.servings),
    date,
  );
  const locked = request.locked.find((entry) => entry.date === date);
  const recipe = locked ? recipeOf(request, locked.recipeId) : chooseDinner(date, state);
  const takers = addOnTakers(recipe, date, request.household.members, {
    catalog: request.book.catalog,
    counts: state.counts,
  });
  const meatAddOns = assignAddOns(recipe, date, takers, state.counts);
  consume(state, [...scaledIngredients(recipe, state.servings.dinner), ...meatAddOns], date);
  countFish(recipe, date, { catalog: request.book.catalog, counts: state.fishPerWeek });
  state.history.push({ date, recipeId: recipe.id, cuisine: recipe.cuisine });
  return {
    date,
    breakfast,
    dinner: {
      recipeId: recipe.id,
      servings: state.servings.dinner,
      meatAddOns,
      locked: Boolean(locked),
    },
  };
}

/**
 * DE: Erster Planentwurf: Tag für Tag das am besten bewertete erlaubte Abendessen, ohne Budgetkorrektur.
 * EN: First plan draft: day by day the best-scored allowed dinner, without budget repair.
 */
export function generateDraft(request: PlanRequest): { plan: Plan; shopping: ShoppingResult } {
  const state = createState(request);
  seedFromPastDays(state);
  const days = datesOf(request.period).map((date) => planDay(date, state));
  const plan: Plan = { period: request.period, days, warnings: state.warnings };
  return { plan, shopping: shopForPlan(plan, request) };
}

/**
 * DE: Plan für einen Monat oder eine Woche, der möglichst ins Budget passt.
 * EN: Plan for a month or a week that fits the budget as well as possible.
 */
export function generatePlan(request: PlanRequest): { plan: Plan; shopping: ShoppingResult } {
  const { plan, shopping } = repairBudget(generateDraft(request), request);
  return { plan: withClosedShopWarnings(plan, shopping.late), shopping };
}
